/**
 * MainLayout.js - Componente principal del layout de la aplicación
 * 
 * Este componente maneja la estructura completa del layout, incluyendo:
 * - Header con navegación principal y búsqueda
 * - Sidebar izquierdo con navegación secundaria
 * - Área de contenido principal
 * - Sidebar derecho con acciones rápidas
 * - Footer con información de estado
 * - Gestión de temas (light/dark mode)
 * - Estados de los sidebars (ancho, colapso)
 */

import React, { useState } from 'react';
import { getTheme } from './theme';  // Importación de temas
import { useThemeContext } from '../../contexts/ThemeContext';
import Header from './Header';                            // Componente del header
import SidebarLeft from './SidebarLeft';                  // Sidebar izquierdo
import ContentArea from './ContentArea';                  // Área de contenido principal
import SidebarRight from './SidebarRight';                // Sidebar derecho
import Footer from './Footer';                            // Footer de la aplicación

/**
 * Componente MainLayout
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Contenido a renderizar en el área principal
 * @param {string} props.logo - Logo/título de la aplicación
 * @param {Array} props.navigationItems - Items de navegación principal
 * @param {string} props.activeNavItem - Item de navegación activo
 * @param {string} props.searchPlaceholder - Placeholder del campo de búsqueda
 * @param {string} props.searchShortcut - Atajo de teclado para búsqueda
 * @param {Object} props.user - Usuario autenticado (para avatar del header)
 * @param {Function} props.onLogout - Callback al cerrar sesión
 */
const MainLayout = ({
  children,                                                // Contenido principal
  // Propiedades del Header
  logo = "Soquimat ERP",                                  // Logo por defecto
  navigationItems = [                                      // Items de navegación por defecto
    { text: "Docs", href: "/docs" },
    { text: "CLI", href: "/cli", active: true },
    { text: "Learn", href: "/learn" }
  ],
  activeNavItem = "CLI",                                  // Item activo por defecto
  searchPlaceholder = "Search...",                        // Placeholder de búsqueda
  searchShortcut = "⌘K",                                 // Atajo de teclado
  user = null,
  onLogout = () => {},
  onRefresh = null,
  
  // Propiedades del Sidebar Izquierdo
  leftSections = [                                        // Secciones del sidebar izquierdo
    {
      key: "getting-started",                             // Identificador único de la sección
      title: "Get Started",                               // Título de la sección
      collapsible: false,                                 // No se puede colapsar
      items: [                                            // Items de navegación
        { key: "overview", text: "Overview", href: "/overview", active: true },
        { key: "installation", text: "Installation", href: "/installation" },
        { key: "quick-start", text: "Quick Start", href: "/quick-start" }
      ]
    },
    {
      key: "headless",                                    // Sección de modo headless
      title: "Headless",
      collapsible: true,                                  // Se puede colapsar
      items: [
        { 
          key: "interactive-mode", 
          text: "Interactive mode", 
          href: "/interactive",
          subItems: [                                     // Sub-items anidados
            { key: "basic-usage", text: "Basic usage", href: "/interactive/basic" },
            { key: "advanced-usage", text: "Advanced usage", href: "/interactive/advanced" }
          ]
        },
        { key: "non-interactive-mode", text: "Non-interactive mode", href: "/non-interactive" },
        { key: "sessions", text: "Sessions", href: "/sessions" }
      ]
    },
    {
      key: "reference",                                   // Sección de referencia
      title: "Reference",
      collapsible: true,
      items: [
        { key: "api", text: "API Reference", href: "/api" },
        { key: "commands", text: "Commands", href: "/commands" },
        { key: "configuration", text: "Configuration", href: "/configuration" }
      ]
    }
  ],
  activeSidebarItem = "Overview",                         // Item activo del sidebar
  adminNav = null,                                        // Enlace fijo inferior: Administración
  footerNavItems = null,                                  // Enlaces fijos al pie del sidebar (agenda, admin)
  
  // Propiedades del Sidebar Derecho
  rightSections = [],
  rightActionButtons = [],
  
  // Propiedades del Footer
  footerStatusItems = [],                                  // Se definen dinámicamente dentro del componente
  
  // Propiedades de Layout
  showLeftSidebar = true,                                 // Mostrar sidebar izquierdo
  showRightSidebar = true,                                // Mostrar sidebar derecho
  showFooter = true,                                      // Mostrar footer
  className = "",                                         // Clase CSS adicional
  ...props                                                // Propiedades adicionales
}) => {
  // Obtener estado del tema del contexto
  const { isDarkMode, colorMode, setColorMode } = useThemeContext();
  const currentTheme = getTheme(colorMode);
  
  // Estados de los sidebars
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(224);     // Ancho del sidebar izquierdo (reducido 20%)
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(true); // Colapsado por defecto
  const [rightSidebarWidth, setRightSidebarWidth] = useState(320);   // Ancho del sidebar derecho

  /**
   * Función para manejar el redimensionamiento del sidebar izquierdo
   * @param {number} newWidth - Nuevo ancho del sidebar
   */
  const handleLeftSidebarResize = (newWidth) => {
    setLeftSidebarWidth(Math.max(200, Math.min(500, newWidth))); // Limitar ancho entre 200px y 500px
  };

  const handleLeftSidebarToggle = () => {
    setLeftSidebarCollapsed((prev) => !prev);
  };

  /**
   * Función para alternar el estado de colapso del sidebar derecho
   */
  const handleRightSidebarToggle = () => {
    setRightSidebarCollapsed(prev => !prev);                     // Cambiar estado de colapso usando función previa
  };

  /**
   * Estilos del contenedor principal
   * Define los estilos base del layout con tema dinámico
   */
  const mainLayoutStyles = {
    minHeight: '100vh',                                          // Altura mínima de la ventana
    backgroundColor: currentTheme.colors.secondary,              // Color de fondo del tema actual
    fontFamily: currentTheme.typography.fontFamily.primary,      // Fuente primaria del tema
    color: currentTheme.colors.text.primary,                     // Color de texto primario
    transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out', // Transición suave para cambios de tema
    ...props.style                                               // Estilos adicionales pasados como props
  };

  void rightSections;
  void rightActionButtons;

  /**
   * Renderizado del componente MainLayout
   * Retorna la estructura JSX completa del layout
   */
  return (
    <div style={mainLayoutStyles} className={className}>
      {/* Header principal con navegación y controles */}
      <Header
        logo={logo}                                              // Logo de la aplicación
        navigationItems={navigationItems}                        // Items de navegación principal
        activeItem={activeNavItem}                               // Item de navegación activo
        searchPlaceholder={searchPlaceholder}                    // Placeholder de búsqueda
        searchShortcut={searchShortcut}                         // Atajo de teclado
        user={user}
        onLogout={onLogout}
        onRefresh={onRefresh}
        onThemeChange={setColorMode}
        colorMode={colorMode}
        isDarkMode={isDarkMode}                                 // Estado del modo oscuro
      />

      {/* Sidebar Izquierdo - Navegación secundaria */}
      {showLeftSidebar && (                                    // Renderizar solo si está habilitado
        <SidebarLeft
          sections={leftSections}                              // Secciones de navegación
          activeItem={activeSidebarItem}                       // Item activo del sidebar
          isDarkMode={isDarkMode}                             // Estado del modo oscuro
          width={leftSidebarWidth}                             // Ancho del sidebar
          collapsed={leftSidebarCollapsed}
          onToggle={handleLeftSidebarToggle}
          onResize={leftSidebarCollapsed ? undefined : handleLeftSidebarResize}
          adminNav={adminNav}
          footerNavItems={footerNavItems}
        />
      )}

      {/* Área de Contenido Principal */}
      <ContentArea 
        leftSidebarWidth={leftSidebarCollapsed ? 0 : leftSidebarWidth}
        rightSidebarWidth={rightSidebarCollapsed ? 0 : rightSidebarWidth} // Ancho del sidebar derecho (0 si está colapsado)
        isDarkMode={isDarkMode}                               // Estado del modo oscuro
      >
        {children}                                             {/* Contenido principal pasado como children */}
      </ContentArea>

      {/* Sidebar Derecho - Saludo, solicitudes, avisos y agenda */}
      {showRightSidebar && (                                   // Renderizar solo si está habilitado
        <SidebarRight
          isDarkMode={isDarkMode}                             // Estado del modo oscuro
          collapsed={rightSidebarCollapsed}                   // Estado de colapso
          width={rightSidebarWidth}                           // Ancho del sidebar
          onToggle={handleRightSidebarToggle}                 // Handler para colapsar/expandir
        />
      )}

      {/* Footer con información de estado */}
      {showFooter && (                                          // Renderizar solo si está habilitado
        <Footer
          statusItems={footerStatusItems}                       // Items de estado (puede ser [])
          isDarkMode={isDarkMode}                              // Estado del modo oscuro
        />
      )}
    </div>
  );
};

/**
 * Exportación del componente MainLayout como componente por defecto
 * Este es el componente principal que orquesta todo el layout de la aplicación
 */
export default MainLayout;
